import React, { useEffect, useMemo, useRef, useState } from 'react';
import cx from 'classnames';
import { Form, Button, ConfigProvider, Spin, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import PPAOnboardingHeader from 'components/PPAOnboardingHeader';
import type { TSelectedPayer } from 'components/CredentialingPayerSteps';
import { getActivePayerList, getGroupProviderPayers } from 'api/credentialing';
import { PATH } from 'constants/path';
import { CALENDLY_LINK } from 'constants/common';
import useCredentialProviders from 'hooks/credentialing/useCredentialProviders';
import useCredentialInfo from 'hooks/credentialing/useCredentialInfo';
import { useGroupCredentialingEnrollment } from 'hooks/credentialing/useGroupCredentialingEnrollment';
import useCheckLeadFormByEmail from 'hooks/ppa/useCheckLeadFormByEmail';
import { isCredentialingHelpType } from 'pages/PPALeadFormPage/helpType';
import ProviderStore from 'store/Provider';
import {
    ECredentialingFlowMode,
    ECredentialingModalStep,
    ENPIType,
    TCredentialingProvider,
    type OrgCredentialingEnrollmentVo,
    type ProviderCredentialingPayerVo,
    type TKlarityPayer,
} from 'types/credentialing';
import { renderCredStep, type CredStepKey } from '../../steps';
import { CredTargetProviderContext } from '../../steps/targetProvider';
import {
    REVIEW_SECTIONS,
    getReviewErrors,
    getSectionErrors,
    getSectionMissingFields,
    fieldMarkableErrors,
} from '../../reviewSpec';
import { saveStepValues, fetchProviderCredValues } from '../../credReviewData';
import ReviewSection from '../ReviewSection';
import BillingTypePanel from '../BillingTypePanel';
import s from '../../s.module.less';
import c from './s.module.less';
import CredentialingModal from 'components/CredentialingModal';
import PracticeEnrollmentSection, { type PracticeEnrollmentSectionHandle } from '../PracticeEnrollmentSection';
import PayerRequirementsPanel from '../PayerRequirementsPanel';
import {
    PAYER_SECTION_TITLES,
    type TSectionKey,
    loadPayerReqData,
    payerRequirementGaps,
} from '../PayerRequirementsPanel/gaps';
import MedicaidGroupSection, { type MedicaidGroupSectionHandle, groupMedicaidMissing } from '../MedicaidGroupSection';

const BannerIcon = () => (
    <svg viewBox="0 0 20 20" fill="currentColor" width="18" height="18" aria-hidden>
        <path
            fillRule="evenodd"
            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-5a.75.75 0 01.75.75v4.5a.75.75 0 01-1.5 0v-4.5A.75.75 0 0110 5zm0 10a1 1 0 100-2 1 1 0 000 2z"
            clipRule="evenodd"
        />
    </svg>
);

const providerLabel = (p: TCredentialingProvider) =>
    `${p.firstName || ''} ${p.lastName || ''}`.trim() || p.email || `Provider ${p.providerId}`;

// Required practice-enrollment fields; any blank one marks the panel "Attention required" and
// blocks submit, matching the per-provider sections. entityType is the raw server value, so a
// custom "Other" text passes the same check.
const ENROLLMENT_REQUIRED: { key: keyof OrgCredentialingEnrollmentVo & string; label: string }[] = [
    { key: 'legalBusinessName', label: 'Legal business name' },
    { key: 'groupNpi', label: 'Group NPI (Type 2)' },
    { key: 'taxId', label: 'Tax ID / EIN' },
    { key: 'entityType', label: 'Entity type' },
    { key: 'practiceLocation', label: 'Practice location' },
    { key: 'w9FileName', label: 'W-9 (group Tax ID)' },
];

type ValuesMap = Record<number, Record<string, any>>;

// 每个 provider 待提交的 payer 选择（DRAFT，panel 还没建）→ 触发计算的输入；payerType 从 payer 列表反查
const loadPendingPayers = async (providerIds: number[]): Promise<Record<number, TSelectedPayer[]>> => {
    const res: any = await getGroupProviderPayers(providerIds);
    const rows: ProviderCredentialingPayerVo[] = res?.data?.data || [];
    const states = Array.from(new Set(rows.map((r) => r.payerState)));
    const optRes: any = states.length ? await getActivePayerList(states).catch(() => null) : null;
    const options: TKlarityPayer[] = optRes?.data?.data || [];
    const map: Record<number, TSelectedPayer[]> = {};
    rows.forEach((r) => {
        if (!map[r.providerId]) map[r.providerId] = [];
        map[r.providerId].push({
            payerName: r.payerName,
            state: r.payerState,
            payerType: options.find((o) => o.id === r.klarityPayerId)?.payerType,
        });
    });
    return map;
};

// group credentialing「Continue to review」跨页面进来的 group review。
// 结构对照原型 showReviewPage()：provider chips 条 + Practice enrollment 块 + 选中 provider 的逐 section review。
// 复用 solo review 的 ReviewSection / reviewSpec / renderCredStep；数据按 providerId 主动拉齐（form 跨页进来为空）。
export default function GroupReviewView() {
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [providers, providersLoading] = useCredentialProviders();
    const [getUser] = ProviderStore.useStore();
    const [leadFormData] = useCheckLeadFormByEmail(getUser('data')?.email || '');
    const [enrollment, , refetchEnrollment] = useGroupCredentialingEnrollment();
    const [credentialInfo] = useCredentialInfo();

    // group review 只纳入已完成 payer 选择的 provider（刚走完代填向导 + 选完 payer，进入待提交状态）
    const reviewProviders = useMemo(
        () => providers.filter((p) => p.onboardingStatus === 'IN_PROGRESS' && p.onboardingStep === 'PAYERS_SELECTED'),
        [providers],
    );

    // 每个 provider 的合并 values（7 步拉齐）；chip 就绪/红点、逐 section 渲染都读它
    const [valuesByProvider, setValuesByProvider] = useState<ValuesMap>({});
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [editingSections, setEditingSections] = useState<CredStepKey[]>([]);
    // Practice enrollment 板块的内联编辑态（由子组件上报），编辑中禁用 Submit
    const [enrollmentEditing, setEnrollmentEditing] = useState(false);
    const [savingSection, setSavingSection] = useState<CredStepKey | null>(null);
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const [valuesLoading, setValuesLoading] = useState(true);
    const [addPayersOpen, setAddPayersOpen] = useState(false);
    const enrollmentSectionRef = useRef<PracticeEnrollmentSectionHandle>(null);
    const medicaidSectionRef = useRef<MedicaidGroupSectionHandle>(null);
    const [medicaidEditing, setMedicaidEditing] = useState(false);
    // Payer requirements：Submit 时按每个 provider 拉最新数据算出没填完的板块（链中途关掉弹窗 / 后来新触发的步骤）
    const [payersByProvider, setPayersByProvider] = useState<Record<number, TSelectedPayer[]>>({});
    const [payerGaps, setPayerGaps] = useState<Record<number, TSectionKey[]>>({});
    const [payerAttention, setPayerAttention] = useState<{ step: TSectionKey; nonce: number } | null>(null);
    const [payerEditing, setPayerEditing] = useState(false);
    const [checking, setChecking] = useState(false);

    // Cancel 内联编辑时用于回滚的整表快照
    const snapshotRef = useRef<Record<string, any> | null>(null);
    const wizardRef = useRef<HTMLDivElement>(null);
    const scrollTop = () => wizardRef.current?.scrollTo({ top: 0 });

    // 把某个 provider 的缓存 values 灌进共享 form（供内联编辑用）
    const loadIntoForm = (id: number, map: ValuesMap) => {
        form.resetFields();
        form.setFieldsValue(map[id] || {});
    };

    // 首次拿到名单后，按 providerId 拉齐每个 provider 的 7 步数据；落在第一个未完成 provider（否则第一个）
    useEffect(() => {
        if (!reviewProviders.length) {
            if (!providersLoading) setValuesLoading(false);
            return undefined;
        }
        let cancelled = false;
        (async () => {
            setValuesLoading(true);
            const [entries, pending] = await Promise.all([
                Promise.all(
                    reviewProviders.map(
                        async (p) => [p.providerId, await fetchProviderCredValues(p.providerId)] as const,
                    ),
                ),
                loadPendingPayers(reviewProviders.map((p) => p.providerId)).catch(() => ({})),
            ]);
            if (cancelled) return;
            setPayersByProvider(pending);
            const map: ValuesMap = {};
            entries.forEach(([id, vals]) => {
                map[id] = vals;
            });
            const firstBad = reviewProviders.find((p) => getReviewErrors(map[p.providerId] || {}).length > 0);
            const landId = (firstBad || reviewProviders[0]).providerId;
            setValuesByProvider(map);
            setSelectedId(landId);
            loadIntoForm(landId, map);
            setValuesLoading(false);
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reviewProviders]);

    const values = selectedId != null ? valuesByProvider[selectedId] || {} : {};
    const selectedPayerGaps = selectedId != null ? payerGaps[selectedId] || [] : [];
    const reviewErrors = [
        ...getReviewErrors(values),
        ...selectedPayerGaps.map((step) => `${PAYER_SECTION_TITLES[step]} not completed`),
    ];
    const showBanner = submitAttempted && reviewErrors.length > 0;

    // Practice-enrollment blanks; like the provider sections, the red border + badge only light
    // up after a submit attempt.
    const enrollmentMissing = useMemo(
        () => ENROLLMENT_REQUIRED.filter((f) => !String(enrollment?.[f.key] || '').trim()),
        [enrollment],
    );
    const enrollmentHasErrors = submitAttempted && enrollmentMissing.length > 0;
    // 10/19：Set up your practice 不拦 Next，按州的 Medicaid group enrollment 漏答 / Yes 没 ID 在这里拦 Submit
    const medicaidMissing = useMemo(() => groupMedicaidMissing(enrollment), [enrollment]);
    const medicaidHasErrors = submitAttempted && medicaidMissing.length > 0;

    // Provider ids handed to the agreement modal so signGroupAgreement / the panel list resolve.
    const reviewProviderIds = useMemo(() => reviewProviders.map((p) => p.providerId), [reviewProviders]);

    const errorCountFor = (id: number) =>
        getReviewErrors(valuesByProvider[id] || {}).length + (payerGaps[id] || []).length;
    const readyCount = useMemo(
        () => reviewProviders.filter((p) => errorCountFor(p.providerId) === 0).length,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [reviewProviders, valuesByProvider, payerGaps],
    );

    const switchProvider = (id: number) => {
        if (id === selectedId) return;
        setSelectedId(id);
        setEditingSections([]);
        setSubmitAttempted(false);
        setPayerAttention(null);
        loadIntoForm(id, valuesByProvider);
        scrollTop();
    };

    const openSectionEdit = (key: CredStepKey) => {
        snapshotRef.current = form.getFieldsValue(true);
        setEditingSections((prev) => (prev.includes(key) ? prev : [...prev, key]));
    };

    const cancelSectionEdit = (key: CredStepKey) => {
        if (snapshotRef.current) form.setFieldsValue(snapshotRef.current);
        setEditingSections((prev) => prev.filter((k) => k !== key));
    };

    // 保存某 section：按选中 provider 落库（透传 providerId 作为 override），再刷新该 provider 的缓存 + 红点。
    // 与 solo review 一致：先用表单实时值做缺失/格式校验；保存失败时报错并保持编辑态，
    // 不更新缓存 —— 否则缓存宣称已保存、后端实际没存上，Submit 的缓存校验会放行脏数据
    const saveSectionEdit = async (key: CredStepKey) => {
        if (selectedId == null) return;
        const current = form.getFieldsValue(true);
        const missing = getSectionMissingFields(current, key);
        if (missing.length > 0) {
            // 标红剔除 NpiRegistryBox 已内联展示的错误，避免同一条错误上下两行红字
            form.setFields(fieldMarkableErrors(missing));
            form.scrollToField(missing[0].name, { behavior: 'smooth', block: 'center' });
            message.error('Please complete the required fields before saving.');
            return;
        }
        setSavingSection(key);
        try {
            const ok = await saveStepValues(key, form, selectedId);
            if (!ok) throw new Error('save failed');
            setValuesByProvider((prev) => ({ ...prev, [selectedId]: form.getFieldsValue(true) }));
            setEditingSections((prev) => prev.filter((k) => k !== key));
            message.success('Changes saved.');
        } catch (e) {
            message.error('Failed to save changes. Please try again.');
        } finally {
            setSavingSection((cur) => (cur === key ? null : cur));
        }
    };

    // 退出按来源回落，与登录/PPALayout 判定 landing-vs-tab 用同一规则：
    // credentialing 类型用户 且 尚未完成 credentialing（无 COMPLETED provider）→ 回 landing；否则回 dashboard credentialing tab。
    // 注意：dashboard tab 里内嵌的 PPACredentialingLandingPage(isTab) 也算 tab，故不能只看路由，须按此规则判定。
    const isCredentialingUser = isCredentialingHelpType(leadFormData?.leadForm?.practiceHelpType);
    const credentialingCompleted = providers.some((p) => p.onboardingStatus === 'COMPLETED');
    const handleExit = () => {
        if (isCredentialingUser && !credentialingCompleted) {
            navigate(PATH.CREDENTIALING_LANDING, { state: { savedExit: true } });
        } else {
            navigate(`${PATH.DASHBOARD}/${PATH.CREDENTIALING}`);
        }
    };
    const handleBack = () => navigate(-1);

    // Submit：同时校验 group 层 Practice enrollment 和跨 provider 的各 section，
    // 一次点击把两层的缺失全部展开标红（不能只展开 enrollment 就 return，否则下面报错的
    // section 要等 enrollment 保存后再点一次 Submit 才出现）；全部完成才进提交弹窗
    const isGroupPractice = credentialInfo.billingNpiType?.value === ENPIType.PRACTICE_NPI;
    const checkPayerGaps = async (
        ids: number[],
        payersMap = payersByProvider,
    ): Promise<Record<number, TSectionKey[]>> => {
        const entries = await Promise.all(
            ids.map(async (id) => {
                try {
                    const data = await loadPayerReqData(id, isGroupPractice);
                    return [id, payerRequirementGaps(payersMap[id] || [], data)] as const;
                } catch (e) {
                    // 拉取失败不拦提交（与 7 步数据 best-effort 一致）
                    return [id, [] as TSectionKey[]] as const;
                }
            }),
        );
        return Object.fromEntries(entries);
    };

    // Payer requirements 板块保存后只重算这一位
    const recheckPayerGaps = async (pid: string) => {
        const id = Number(pid);
        const next = await checkPayerGaps([id]);
        setPayerGaps((prev) => ({ ...prev, ...next }));
    };

    const handleSubmit = async () => {
        setChecking(true);
        let gapsMap: Record<number, TSectionKey[]>;
        try {
            // payer 选择也重拉：页面加载时那次失败会让触发计算退化成只看已存数据，漏拦没填的板块
            const freshPayers = await loadPendingPayers(reviewProviderIds).catch(() => payersByProvider);
            setPayersByProvider(freshPayers);
            gapsMap = await checkPayerGaps(reviewProviderIds, freshPayers);
        } finally {
            setChecking(false);
        }
        setPayerGaps(gapsMap);
        const badCount = (id: number) =>
            getReviewErrors(valuesByProvider[id] || {}).length + (gapsMap[id] || []).length;
        const enrollmentBad = enrollmentMissing.length > 0;
        const medicaidBad = medicaidMissing.length > 0;
        const firstBad = reviewProviders.find((p) => badCount(p.providerId) > 0);
        if (!enrollmentBad && !medicaidBad && !firstBad) {
            setAddPayersOpen(true);
            return;
        }
        setSubmitAttempted(true);
        if (enrollmentBad) enrollmentSectionRef.current?.openEdit();
        if (medicaidBad) medicaidSectionRef.current?.openEdit();
        const vals = firstBad ? valuesByProvider[firstBad.providerId] || {} : {};
        const incomplete = firstBad
            ? REVIEW_SECTIONS.filter((sec) => getSectionErrors(vals, sec.key).length).map((sec) => sec.key)
            : [];
        const firstPayerGap = firstBad ? (gapsMap[firstBad.providerId] || [])[0] : undefined;
        if (firstBad) {
            if (firstBad.providerId !== selectedId) {
                setSelectedId(firstBad.providerId);
                loadIntoForm(firstBad.providerId, valuesByProvider);
            }
            snapshotRef.current = vals;
            setEditingSections(incomplete);
            // panel 一次只能展开一个板块：展开第一个没填完的，其余标红
            setPayerAttention(firstPayerGap ? { step: firstPayerGap, nonce: Date.now() } : null);
        }
        scrollTop();
        // 编辑态展开后字段才挂载，下一帧再触发校验/标红缺失项
        setTimeout(() => {
            if (enrollmentBad) enrollmentSectionRef.current?.validate();
            incomplete.forEach((key) => form.setFields(fieldMarkableErrors(getSectionMissingFields(vals, key))));
            // enrollment 有缺失时停在顶部（enrollment 区块就在页面上方），否则滚到第一个报错 section
            const firstSection = incomplete[0] || firstPayerGap;
            if (!enrollmentBad && !medicaidBad && firstSection) {
                const first = document.querySelector(`[data-section="${firstSection}"]`);
                if (first) first.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }, 60);
    };

    const pageLoading = providersLoading || valuesLoading;

    return (
        <ConfigProvider theme={{ token: { colorPrimary: '#0E62F4' } }}>
            <div className={s.wizardPage} ref={wizardRef}>
                <div className={s.topbar}>
                    <PPAOnboardingHeader webinarLink={CALENDLY_LINK.KLARITY_BILLING_WEBINAR} />
                </div>

                <div className={`${s.wizHeader} mt-6`}>
                    <div className={s.wizTitle}>Review your credentialing before you submit</div>
                    <div className={s.wizSubtitle}>
                        Please confirm your information before submitting. You can always edit later if something
                        changes.
                    </div>
                </div>

                <Spin spinning={pageLoading}>
                    <CredTargetProviderContext.Provider value={selectedId ?? undefined}>
                        <Form form={form} layout="vertical" requiredMark={false} className={s.wizBody}>
                            {/* Provider chips：切换下方展示哪个 provider；红点表示该 provider 有必填缺失 */}
                            {reviewProviders.length > 0 && (
                                <div className={c.providerBar}>
                                    <div className={c.providerSummary}>
                                        {readyCount} of {reviewProviders.length} providers ready to submit
                                    </div>
                                    <div className={c.providerChips}>
                                        {reviewProviders.map((p) => (
                                            <button
                                                type="button"
                                                key={p.providerId}
                                                className={cx(c.providerChip, p.providerId === selectedId && c.active)}
                                                onClick={() => switchProvider(p.providerId)}
                                            >
                                                {providerLabel(p)}
                                                {errorCountFor(p.providerId) > 0 && <span className={c.chipDot} />}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Billing type（group 层）：Change billing type 回 start 入口重新选择，
                            repick 参数让 start 页跳过续填判定停在入口 */}
                            <BillingTypePanel
                                variant="group"
                                otherProvidersCount={Math.max(reviewProviders.length - 1, 0)}
                                onChangeBillingType={() => navigate(`${PATH.CREDENTIALING_START}?repick=1`)}
                            />

                            {/* Practice enrollment（group 层，整个 practice 只展示一次） */}
                            <PracticeEnrollmentSection
                                ref={enrollmentSectionRef}
                                hasErrors={enrollmentHasErrors}
                                footerOffset
                                onSaved={refetchEnrollment}
                                onEditingChange={setEnrollmentEditing}
                            />

                            {/* Medicaid group enrollment（practice 层，practice payers 有 Medicaid 时才出现） */}
                            <MedicaidGroupSection
                                ref={medicaidSectionRef}
                                hasErrors={medicaidHasErrors}
                                footerOffset
                                onSaved={refetchEnrollment}
                                onEditingChange={setMedicaidEditing}
                            />

                            {/* 选中 provider 的逐 section review（复用 solo 的 ReviewSection / reviewSpec / renderCredStep） */}
                            {selectedId != null && (
                                <>
                                    {showBanner && (
                                        <div className={s.reviewErrorBanner}>
                                            <BannerIcon />
                                            <div>
                                                <div className={s.reviewErrorTitle}>
                                                    Please complete the following before submitting
                                                </div>
                                                <ul>
                                                    {reviewErrors.map((e) => (
                                                        <li key={e}>{e}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </div>
                                    )}
                                    {REVIEW_SECTIONS.map((sec) => (
                                        <ReviewSection
                                            key={sec.key}
                                            sectionKey={sec.key}
                                            title={sec.title}
                                            values={values}
                                            editing={editingSections.includes(sec.key)}
                                            saving={savingSection === sec.key}
                                            footerOffset
                                            hasErrors={submitAttempted && getSectionErrors(values, sec.key).length > 0}
                                            onEdit={() => openSectionEdit(sec.key)}
                                            onCancel={() => cancelSectionEdit(sec.key)}
                                            onSave={() => saveSectionEdit(sec.key)}
                                        >
                                            {renderCredStep(sec.key)}
                                        </ReviewSection>
                                    ))}
                                    {/* V2：payer 触发四步的信息板块（有数据才显示），随选中 provider 切换 */}
                                    <PayerRequirementsPanel
                                        providerId={selectedId}
                                        footerOffset
                                        payers={payersByProvider[selectedId]}
                                        hasErrors={submitAttempted}
                                        attention={payerAttention}
                                        onEditingChange={setPayerEditing}
                                        onSaved={recheckPayerGaps}
                                    />
                                </>
                            )}
                        </Form>
                    </CredTargetProviderContext.Provider>
                </Spin>

                <div className={s.footer}>
                    <div className={s.footerInner}>
                        <button type="button" className={s.exitBtn} onClick={handleExit}>
                            Save and exit
                        </button>
                        <div className={s.footerRight}>
                            <button type="button" className={s.backBtn} onClick={handleBack}>
                                Back
                            </button>
                            <Button
                                type="primary"
                                size="large"
                                className={s.nextBtn}
                                onClick={handleSubmit}
                                loading={checking}
                                // 有 section / practice enrollment 正在内联编辑时禁止提交（须先 Save changes / Cancel），
                                // 与 solo review 一致；否则 Submit 会用旧缓存校验，把刚填未保存的值误标成必填缺失
                                disabled={
                                    editingSections.length > 0 || enrollmentEditing || medicaidEditing || payerEditing
                                }
                            >
                                Submit application
                            </Button>
                        </div>
                    </div>
                </div>
                <CredentialingModal
                    open={addPayersOpen}
                    onClose={() => setAddPayersOpen(false)}
                    flowMode={ECredentialingFlowMode.INITIAL}
                    targetStep={ECredentialingModalStep.REVIEW_AGREEMENT}
                    targetProviderIds={reviewProviderIds}
                    onEnd={() => navigate(PATH.CREDENTIALING_SUBMITTED)}
                />
            </div>
        </ConfigProvider>
    );
}
